import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaBell, FaBriefcase, FaFileInvoiceDollar, FaCheckCircle,
  FaPlay, FaStop, FaExclamationTriangle, FaCheck
} from 'react-icons/fa';
import MainHeader from '../../shared/MainHeader/MainHeader';
import { getApiEndpoint } from '../../../config/api';
import { getDashboardRoute } from '../../../utils/roleUtils';
import './NotificationsPage.css';

const ICON_MAP = {
  job_invite: FaBriefcase,
  job_assigned: FaBriefcase,
  quote_received: FaFileInvoiceDollar,
  quote_approved: FaCheckCircle,
  quote_declined: FaExclamationTriangle,
  job_started: FaPlay,
  job_ended: FaStop,
  conflict: FaExclamationTriangle,
};

const ICON_COLOR_MAP = {
  job_invite: 'blue',
  job_assigned: 'blue',
  quote_received: 'amber',
  quote_approved: 'green',
  quote_declined: 'red',
  job_started: 'green',
  job_ended: 'gray',
  conflict: 'red',
};

const FILTER_TABS = [
  { key: 'all', label: 'All' },
  { key: 'unread', label: 'Unread' },
  { key: 'jobs', label: 'Jobs' },
  { key: 'quotes', label: 'Quotes' },
];

function timeAgo(ts) {
  const diff = (Date.now() - new Date(ts).getTime()) / 1000;
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(ts).toLocaleDateString();
}

function matchesFilter(notification, filter) {
  if (filter === 'all') return true;
  if (filter === 'unread') return !notification.is_read;
  if (filter === 'jobs') return ['job_invite', 'job_assigned', 'job_started', 'job_ended', 'conflict'].includes(notification.type);
  if (filter === 'quotes') return ['quote_received', 'quote_approved', 'quote_declined'].includes(notification.type);
  return true;
}

const NotificationsPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [markingAll, setMarkingAll] = useState(false);

  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(getApiEndpoint('/api/notifications?limit=50'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      setNotifications(data.notifications || []);
      setUnreadCount(data.unreadCount || 0);
      setError(null);
    } catch (e) {
      setError('Could not load notifications. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const markRead = async (id) => {
    try {
      await fetch(getApiEndpoint(`/api/notifications/${id}/read`), {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (e) { /* silent */ }
  };

  const markAllRead = async () => {
    setMarkingAll(true);
    try {
      await fetch(getApiEndpoint('/api/notifications/read-all'), {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (e) { /* silent */ }
    setMarkingAll(false);
  };

  const handleNotificationClick = (notification) => {
    if (!notification.is_read) markRead(notification.id);
  };

  const filtered = notifications.filter(n => matchesFilter(n, activeFilter));
  const hasData = !loading && !error && notifications.length > 0;

  /* ── Skeleton row for loading state ── */
  const SkeletonRow = () => (
    <div className="notif-row notif-skeleton" aria-hidden="true">
      <div className="notif-icon-wrap notif-skel-circle" />
      <div className="notif-body">
        <div className="notif-skel-line notif-skel-title" />
        <div className="notif-skel-line notif-skel-msg" />
        <div className="notif-skel-line notif-skel-time" />
      </div>
    </div>
  );

  return (
    <div className="notif-page">
      <MainHeader />

      <main className="notif-content">
        {/* Page title bar */}
        <div className="notif-page-header">
          <h1 className="notif-page-title">
            <FaBell className="notif-page-title-icon" />
            Notifications
            {hasData && (
              <span className="notif-page-count">{notifications.length}</span>
            )}
          </h1>
          {unreadCount > 0 && !loading && (
            <button
              className="notif-mark-all-btn"
              onClick={markAllRead}
              disabled={markingAll}
            >
              <FaCheck /> {markingAll ? 'Marking…' : 'Mark all read'}
            </button>
          )}
        </div>

        {/* Filter tabs */}
        {hasData && (
          <div className="notif-filters" role="tablist" aria-label="Filter notifications">
            {FILTER_TABS.map(tab => {
              const count = tab.key === 'unread' ? unreadCount :
                tab.key === 'all' ? notifications.length :
                  notifications.filter(n => matchesFilter(n, tab.key)).length;
              return (
                <button
                  key={tab.key}
                  role="tab"
                  aria-selected={activeFilter === tab.key}
                  className={`notif-filter-chip ${activeFilter === tab.key ? 'active' : ''}`}
                  onClick={() => setActiveFilter(tab.key)}
                >
                  {tab.label}
                  {count > 0 && <span className="notif-filter-count">{count}</span>}
                </button>
              );
            })}
          </div>
        )}

        {/* Loading state */}
        {loading && (
          <div className="notif-list">
            {[1, 2, 3, 4, 5].map(i => <SkeletonRow key={i} />)}
          </div>
        )}

        {/* Error state */}
        {!loading && error && (
          <div className="notif-empty-wrap">
            <div className="notif-empty-card">
              <FaExclamationTriangle className="notif-empty-icon notif-empty-icon-red" />
              <h2 className="notif-empty-title">Something went wrong</h2>
              <p className="notif-empty-desc">{error}</p>
              <button className="notif-empty-btn" onClick={() => { setLoading(true); setError(null); fetchNotifications(); }}>
                Retry
              </button>
            </div>
          </div>
        )}

        {/* Empty state — no notifications at all */}
        {!loading && !error && notifications.length === 0 && (
          <div className="notif-empty-wrap">
            <div className="notif-empty-card">
              <FaBell className="notif-empty-icon" />
              <h2 className="notif-empty-title">No notifications yet</h2>
              <p className="notif-empty-desc">
                You're all caught up. Notifications about jobs, quotes, and updates will appear here.
              </p>
              <button className="notif-empty-btn" onClick={() => navigate(getDashboardRoute())}>
                Back to Dashboard
              </button>
            </div>
          </div>
        )}

        {/* Filtered empty state */}
        {hasData && filtered.length === 0 && (
          <div className="notif-empty-wrap">
            <div className="notif-empty-card">
              <FaBell className="notif-empty-icon" />
              <h2 className="notif-empty-title">No {activeFilter} notifications</h2>
              <p className="notif-empty-desc">Try a different filter.</p>
              <button className="notif-empty-btn" onClick={() => setActiveFilter('all')}>
                Show all
              </button>
            </div>
          </div>
        )}

        {/* Notification list */}
        {hasData && filtered.length > 0 && (
          <ul className="notif-list" role="list" aria-label="Notifications">
            {filtered.map(notification => {
              const Icon = ICON_MAP[notification.type] || FaBell;
              const colorClass = ICON_COLOR_MAP[notification.type] || 'gray';
              const isUnread = !notification.is_read;

              return (
                <li key={notification.id} className="notif-row-wrapper">
                  <button
                    className={`notif-row ${isUnread ? 'notif-unread' : ''}`}
                    onClick={() => handleNotificationClick(notification)}
                    aria-label={`${isUnread ? 'Unread: ' : ''}${notification.title}`}
                  >
                    {isUnread && <span className="notif-unread-bar" aria-hidden="true" />}

                    <div className={`notif-icon-wrap notif-icon-${colorClass}`}>
                      <Icon />
                    </div>

                    <div className="notif-body">
                      <div className="notif-title-row">
                        <span className="notif-title">{notification.title}</span>
                        <span className="notif-time">{timeAgo(notification.created_at)}</span>
                      </div>
                      <p className="notif-msg">{notification.message}</p>
                    </div>

                    {isUnread && <span className="notif-dot" aria-label="Unread" />}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </main>
    </div>
  );
};

export default NotificationsPage;
